import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  movementPlansList: [],
  currentMovementPlan: null,
  // { planId, defaults } — whose plan the held defaults belong to, so a
  // response for a plan already navigated away from can be ignored.
  prescriptionDefaults: null,
  enrollmentDetail: null,
  enrollmentDetailLoading: false,
  companyEnrollments: [],
  companyEnrollmentsLoading: false,
  listLoading: false,
  isLoading: false,
  totalCount: 0,
};

export const getAllMovementPlans = createAsyncThunk(
  "movementPlans/getAll",
  async ({ searchQuery = "", page, rowsPerPage, selectedAccount, archive = false }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans?q=${encodeURIComponent(searchQuery)}&page=${page + 1}&per_page=${rowsPerPage}&account_id=${selectedAccount}&archived=${archive}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${bearerToken}` } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// What this examiner last prescribed for each of the plan's movements, so the
// Send page can open with their own numbers rather than blank fields. Personal
// to whoever is asking — the server derives it from the token, not a
// parameter — so it is held apart from the plan itself.
export const getPrescriptionDefaults = createAsyncThunk(
  "movementPlans/prescriptionDefaults",
  async ({ planId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}/prescription_defaults?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${bearerToken}` } });
      const body = response.data?.data ?? response.data;
      return { planId, defaults: body?.prescription_defaults ?? {} };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getMovementPlan = createAsyncThunk(
  "movementPlans/getOne",
  async ({ planId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${bearerToken}` } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMovementPlan = createAsyncThunk(
  "movementPlans/create",
  async ({ movementPlan, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, { movement_plan: movementPlan }, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateMovementPlan = createAsyncThunk(
  "movementPlans/update",
  async ({ planId, movementPlan, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.put(url, { movement_plan: movementPlan }, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const archiveMovementPlan = createAsyncThunk(
  "movementPlans/archive",
  async ({ planId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}/archive?account_id=${selectedAccount}`;
    try {
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${bearerToken}` } });
      toast.success("Movement plan archived successfully");
      return { planId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to archive" });
    }
  }
);

export const unarchiveMovementPlan = createAsyncThunk(
  "movementPlans/unarchive",
  async ({ planId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}/unarchive?account_id=${selectedAccount}`;
    try {
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${bearerToken}` } });
      toast.success("Movement plan unarchived successfully");
      return { planId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to unarchive" });
    }
  }
);

// One send, one group, any number of that group's workers. Each entry in
// `workers` is that worker's prescription already resolved — the shared values
// merged with their own overrides — because that merge is what the examiner
// was looking at when they pressed Send.
//
// A partial success is still a 200: the enrollments that were created exist,
// and treating it as a failure would invite a retry that sends twice. Whoever
// couldn't be sent to comes back named, and the caller shows them.
export const sendMovementPlan = createAsyncThunk(
  "movementPlans/send",
  async ({ planId, groupId, workers = [], selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}/send_plan?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, { group_id: groupId, workers }, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      const data = response.data || {};
      toast.success(data.message || "Plan sent successfully");
      (data.failed || []).forEach((f) => toast.warning(`${f.name}: ${f.reason}`));
      return { planId, sent: data.sent || [], failed: data.failed || [] };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to send plan" });
    }
  }
);

// Re-mails an enrollment that already exists. Distinct from sendMovementPlan,
// which creates one: resending must not mint a second enrollment, or the
// worker gets a different link and their existing answers are stranded on the
// old one. Nothing in the store changes, so there are no reducers for it.
export const resendPlanEnrollment = createAsyncThunk(
  "movementPlans/resendEnrollment",
  async ({ enrollmentId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/plan_enrollments/${enrollmentId}/resend?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, {}, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to resend the plan" });
    }
  }
);

// Closing an enrollment to further writes, and undoing that. Both return the
// new status; nothing else about the enrollment changes, so the detail payload
// is refetched by the caller rather than patched here.
const enrollmentStateChange = (name, action) => createAsyncThunk(
  `movementPlans/${name}`,
  // `feedback` is the examiner's closing note; only `complete` takes one, and
  // the server ignores it elsewhere.
  async ({ enrollmentId, selectedAccount, feedback }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/plan_enrollments/${enrollmentId}/${action}?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, feedback ? { feedback } : {}, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: `Failed to ${action} the plan session` });
    }
  }
);

export const completeEnrollment = enrollmentStateChange("completeEnrollment", "complete");
export const reopenEnrollment   = enrollmentStateChange("reopenEnrollment", "reopen");

export const createMovementPlanItem = createAsyncThunk(
  "movementPlans/createMovementPlanItem",
  async ({ planId, movementId, movementTitle, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}/movement_plan_items?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(
        url,
        { movement_plan_item: { movement_id: movementId } },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const reorderMovementPlanItems = createAsyncThunk(
  "movementPlans/reorderMovementPlanItems",
  async ({ planId, itemId, position, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    try {
      await axios.put(
        `${apiBaseUrl}/movement_plans/${planId}/movement_plan_items/${itemId}?account_id=${selectedAccount}`,
        { movement_plan_item: { position } },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` } }
      );
      return { itemId, position };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMovementPlanItem = createAsyncThunk(
  "movementPlans/deleteMovementPlanItem",
  async ({ planId, itemId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/movement_plans/${planId}/movement_plan_items/${itemId}?account_id=${selectedAccount}`;
    try {
      await axios.delete(url, { headers: { Authorization: `Bearer ${bearerToken}` } });
      return { itemId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getCompanyEnrollments = createAsyncThunk(
  "movementPlans/getCompanyEnrollments",
  async ({ selectedAccount, archive = false, planId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    // archive=false is everything not archived, completed sessions included.
    // planId narrows the list to one plan's sessions, for the copy of this
    // listing embedded on that plan's page.
    let url = `${apiBaseUrl}/plan_enrollments?account_id=${selectedAccount}&archive=${archive}`;
    if (planId) url += `&plan_id=${planId}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${bearerToken}` } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Files a session away, or brings it back. Archiving keeps everything — the
// answers, the status — it just stops the session counting: out of the live
// list, out of history and comparison, and the worker's link stops opening.
export const setEnrollmentArchived = createAsyncThunk(
  "movementPlans/setEnrollmentArchived",
  async ({ enrollmentId, selectedAccount, archived }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const action = archived ? "archive" : "unarchive";
    const url = `${apiBaseUrl}/plan_enrollments/${enrollmentId}/${action}?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, {}, { headers: { Authorization: `Bearer ${bearerToken}` } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getEnrollmentDetail = createAsyncThunk(
  "movementPlans/getEnrollmentDetail",
  async ({ enrollmentId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/plan_enrollments/${enrollmentId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${bearerToken}` } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Several field values in one request, for the examiner's per-movement
// Submit. All-or-nothing on the backend (see PlanEnrollmentsController's
// update_fields_batch): a half-applied Submit would leave the examiner unsure
// what actually saved, so a rejected entry rolls the whole batch back and
// names the field that failed.
export const updateEnrollmentFields = createAsyncThunk(
  "movementPlans/updateEnrollmentFields",
  async ({ enrollmentId, fields, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/plan_enrollments/${enrollmentId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.patch(url, { fields }, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Sets status and/or dose/frequency on this worker's EnrollmentMovementItem for one
// movement — the examiner-only counterpart to updateEnrollmentField (which
// targets a single TestFunction's value instead). Only send the ones
// actually changing; the backend leaves the other alone (see
// PlanEnrollment#upsert_movement_item!).
export const updateMovementItem = createAsyncThunk(
  "movementPlans/updateMovementItem",
  async ({ enrollmentId, movementPlanItemId, status, frequency, doseValue, doseUnit, hidden, targetReps, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/plan_enrollments/${enrollmentId}?account_id=${selectedAccount}`;
    try {
      const body = { movement_plan_item_id: movementPlanItemId };
      if (status !== undefined) body.status = status;
      if (frequency !== undefined) body.frequency = frequency;
      // Only sent when it changed — the server leaves an untouched load alone
      // precisely because the key is absent (see #update_movement_item).
      if (doseValue !== undefined) {
        body.dose_value = doseValue;
        body.dose_unit = doseUnit;
      }
      // Same rule for hiding: absent means "leave it as it is", so a frequency save
      // can't quietly bring a hidden movement back.
      if (hidden !== undefined) body.hidden = hidden;
      // And the rep goal: absent leaves it alone, empty clears it.
      if (targetReps !== undefined) body.target_reps = targetReps;
      const response = await axios.patch(url, body, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const movementPlanSlice = createSlice({
  name: "movementPlan",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllMovementPlans.pending, (state) => { state.listLoading = true; })
      .addCase(getAllMovementPlans.fulfilled, (state, action) => {
        state.listLoading = false;
        state.movementPlansList = action.payload.movement_plans;
        state.totalCount = action.payload.meta?.total_count || 0;
      })
      .addCase(getAllMovementPlans.rejected, (state) => { state.listLoading = false; })
      .addCase(getMovementPlan.pending, (state) => { state.isLoading = true; })
      .addCase(getMovementPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentMovementPlan = action.payload;
      })
      .addCase(getMovementPlan.rejected, (state) => { state.isLoading = false; })
      .addCase(getPrescriptionDefaults.fulfilled, (state, action) => {
        state.prescriptionDefaults = action.payload;
      })
      // Nothing to offer is the same as having nothing — the page opens blank,
      // which is where it started before any of this existed. Recorded against
      // the plan all the same: "no defaults for this plan" is an answer, and
      // the page waits for an answer before it seeds. Left as null it would
      // wait forever.
      .addCase(getPrescriptionDefaults.rejected, (state, action) => {
        state.prescriptionDefaults = { planId: action.meta.arg.planId, defaults: {} };
      })
      .addCase(createMovementPlanItem.pending, (state, action) => {
        const { movementId, movementTitle } = action.meta.arg;
        if (state.currentMovementPlan) {
          state.currentMovementPlan.movement_plan_items = [
            ...(state.currentMovementPlan.movement_plan_items || []),
            { id: `temp_${movementId}`, movement_id: movementId, movement_title: movementTitle },
          ];
        }
      })
      .addCase(createMovementPlanItem.fulfilled, (state, action) => {
        const { movementId } = action.meta.arg;
        if (state.currentMovementPlan) {
          state.currentMovementPlan.movement_plan_items = state.currentMovementPlan.movement_plan_items.map(
            (item) => (item.id === `temp_${movementId}` ? action.payload : item)
          );
        }
      })
      .addCase(createMovementPlanItem.rejected, (state, action) => {
        const { movementId } = action.meta.arg;
        if (state.currentMovementPlan) {
          state.currentMovementPlan.movement_plan_items = state.currentMovementPlan.movement_plan_items.filter(
            (item) => item.id !== `temp_${movementId}`
          );
        }
      })
      .addCase(reorderMovementPlanItems.pending, (state, action) => {
        const { itemId, position } = action.meta.arg;
        if (state.currentMovementPlan) {
          const items = [...state.currentMovementPlan.movement_plan_items];
          const from  = items.findIndex((item) => item.id === itemId);
          if (from !== -1) {
            const [moved] = items.splice(from, 1);
            items.splice(position, 0, moved);
            state.currentMovementPlan.movement_plan_items = items;
          }
        }
      })
      .addCase(reorderMovementPlanItems.fulfilled, () => {})
      .addCase(reorderMovementPlanItems.rejected, () => {})
      .addCase(deleteMovementPlanItem.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        if (state.currentMovementPlan) {
          state.currentMovementPlan.movement_plan_items = state.currentMovementPlan.movement_plan_items.filter(
            (item) => item.id !== itemId
          );
        }
      })
      .addCase(deleteMovementPlanItem.fulfilled, () => {})
      .addCase(deleteMovementPlanItem.rejected, () => {})
      .addCase(archiveMovementPlan.fulfilled, (state, action) => {
        state.movementPlansList = state.movementPlansList.filter((p) => p.id !== action.payload.planId);
      })
      .addCase(unarchiveMovementPlan.fulfilled, (state, action) => {
        state.movementPlansList = state.movementPlansList.filter((p) => p.id !== action.payload.planId);
      })
      .addCase(getCompanyEnrollments.pending,   (state) => { state.companyEnrollmentsLoading = true; })
      .addCase(getCompanyEnrollments.fulfilled, (state, action) => { state.companyEnrollmentsLoading = false; state.companyEnrollments = action.payload; })
      .addCase(getCompanyEnrollments.rejected,  (state) => { state.companyEnrollmentsLoading = false; })
      .addCase(getEnrollmentDetail.pending,   (state) => { state.enrollmentDetailLoading = true; })
      .addCase(getEnrollmentDetail.fulfilled, (state, action) => { state.enrollmentDetailLoading = false; state.enrollmentDetail = action.payload; })
      .addCase(getEnrollmentDetail.rejected,  (state) => { state.enrollmentDetailLoading = false; })
      .addCase(updateEnrollmentFields.fulfilled, (state, action) => {
        const movements = state.enrollmentDetail?.movements;
        if (!movements) return;
        for (const saved of action.payload?.fields ?? []) {
          for (const movement of movements) {
            const fn = movement.functions.find((f) => f.test_function_id === saved.test_function_id);
            if (!fn) continue;
            if (fn.data_type === "Image") fn.value_image_url = saved.value_image_url ?? null;
            else                          fn.value = saved.field_value;
            break;
          }
        }
      })
      // The saved row comes back shaped like the movement it belongs to (see
      // PlanEnrollmentsController#update_movement_item), so every key it
      // carries is copied across. It has to be all of them: the examiner's
      // panel drops its drafts the moment Submit succeeds and reads these
      // values instead, so anything left stale here paints the old value
      // straight back over what was just typed.
      .addCase(updateMovementItem.fulfilled, (state, action) => {
        const { movementPlanItemId } = action.meta.arg;
        const movement = state.enrollmentDetail?.movements?.find((m) => m.movement_plan_item_id === movementPlanItemId);
        if (!movement) return;
        const saved = action.payload;
        if (!saved) return;
        // dose_value and dose_unit are one answer — the server nulls the unit
        // along with the dose — so they're copied together rather than each
        // being guarded on its own.
        for (const key of ["status", "frequency", "dose_value", "dose_unit", "target_reps"]) {
          if (saved[key] !== undefined) movement[key] = saved[key];
        }
      });
  },
});

export default movementPlanSlice.reducer;
